import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  InputAdornment,
  Stack,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import { useStores } from "../../../use-stores";

export interface AddBalanceSubmitPayload {
  amount: number;
  invoice_number: string;
  transaction_id: string;
}

interface AddBalanceModalProps {
  open: boolean;
  storeId?: number | string;
  onClose: () => void;
  onSubmit: (payload: AddBalanceSubmitPayload) => Promise<void>;
}

const formatInr = (value: number) =>
  `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** Optional field — empty is allowed; non-empty must be a valid reference. */
const isValidInvoiceNumber = (value: string): boolean => {
  const trimmed = (value || "").trim();
  if (!trimmed) return true;
  if (trimmed.length < 2 || trimmed.length > 100) return false;
  return /^[A-Za-z0-9][A-Za-z0-9\-_/.@]{1,99}$/.test(trimmed);
};

const INVOICE_NUMBER_ERROR =
  "Invalid invoice number. Use 2–100 characters: letters, numbers, - _ / . @ (e.g. INV-2026-001).";

const AddBalanceModal: React.FC<AddBalanceModalProps> = ({
  open,
  storeId,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const { swagStoreStore } = useStores();

  const [formData, setFormData] = useState({
    amount: "",
    invoiceNumber: "",
    transactionId: "",
  });
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFormData({ amount: "", invoiceNumber: "", transactionId: "" });
    setSubmitError(null);
  }, [open, storeId]);

  const trimmedInvoiceNumber = formData.invoiceNumber.trim();
  const invoiceNumberInvalid =
    trimmedInvoiceNumber.length > 0 &&
    !isValidInvoiceNumber(trimmedInvoiceNumber);

  const amountValue = Number(formData.amount);
  const canSubmit =
    Number.isFinite(amountValue) &&
    amountValue > 0 &&
    !invoiceNumberInvalid &&
    !swagStoreStore.addBalanceSubmitting;

  const handleAmountPaid = async () => {
    if (!canSubmit) return;
    if (invoiceNumberInvalid) {
      setSubmitError(INVOICE_NUMBER_ERROR);
      return;
    }
    setSubmitError(null);

    try {
      await onSubmit({
        amount: amountValue,
        invoice_number: trimmedInvoiceNumber,
        transaction_id: formData.transactionId.trim(),
      });
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message || err?.message || "Failed to add balance.",
      );
    }
  };

  const currentBalance =
    swagStoreStore.addBalanceCurrentBalance != null
      ? formatInr(swagStoreStore.addBalanceCurrentBalance)
      : swagStoreStore.addBalanceWalletLoading
        ? "Loading..."
        : "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors?.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.customColors?.white ?? "#fff"} 0%, ${theme.palette.customColors?.lightGreyColor ?? "#f5f5f5"} 100%)`,
          },
        },
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
        <CustomTypography variant="heading5">Add Balance</CustomTypography>
        <CustomTypography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, display: "block" }}
        >
          Record payments and credit the customer wallet.
        </CustomTypography>
      </Box>
      <Divider sx={{ mt: 2, borderColor: theme.palette.customColors?.lightBorder }} />

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Box
          sx={{
            mb: 2.5,
            borderRadius: 1,
            border: `1px solid ${theme.palette.customColors?.lightBorder ?? theme.palette.divider}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              bgcolor: theme.palette.customColors?.greyBackground ?? "action.hover",
            }}
          >
            <Box sx={{ px: 2, py: 1.25, borderRight: `1px solid ${theme.palette.customColors?.lightBorder ?? theme.palette.divider}` }}>
              <CustomTypography variant="body2" color="text.secondary">
                Current balance
              </CustomTypography>
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              {swagStoreStore.addBalanceWalletLoading && (
                <CircularProgress size={16} />
              )}
              <CustomTypography variant="heading8" fontWeight={700}>
                {currentBalance}
              </CustomTypography>
            </Box>
          </Box>
        </Box>

        {swagStoreStore.addBalanceWalletError && (
          <CustomTypography
            variant="body2"
            color="error"
            sx={{ mb: 2, display: "block" }}
          >
            {swagStoreStore.addBalanceWalletError}
          </CustomTypography>
        )}

        <Stack spacing={2.5}>
          <FormSmallTextField
            title="Custom amount"
            type="number"
            value={formData.amount}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, amount: value ?? "" }))
            }
            startAdornment={
              <InputAdornment position="start">
                <Box component="span" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                  ₹
                </Box>
              </InputAdornment>
            }
            sx={{ width: "100%" }}
          />
          <FormSmallTextField
            title="Invoice number"
            value={formData.invoiceNumber}
            maxLength={100}
            onChange={(_, value) => {
              setFormData((prev) => ({ ...prev, invoiceNumber: value ?? "" }));
              if (submitError === INVOICE_NUMBER_ERROR) {
                setSubmitError(null);
              }
            }}
            error={invoiceNumberInvalid}
            errorMessage={INVOICE_NUMBER_ERROR}
            sx={{ width: "100%" }}
          />
          <FormSmallTextField
            title="Transaction id"
            value={formData.transactionId}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, transactionId: value ?? "" }))
            }
            sx={{ width: "100%" }}
          />
        </Stack>

        {submitError && (
          <CustomTypography
            variant="body2"
            color="error"
            sx={{ mt: 2, display: "block" }}
          >
            {submitError}
          </CustomTypography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.customColors?.lightBorder ?? theme.palette.divider}`,
        }}
      >
        <AppButton
          label="Cancel"
          variant="grayscale"
          onClick={onClose}
          disabled={swagStoreStore.addBalanceSubmitting}
        />
        <AppButton
          label={
            swagStoreStore.addBalanceSubmitting ? "Saving..." : "Amount Paid"
          }
          variant="primary"
          onClick={handleAmountPaid}
          disabled={!canSubmit}
          customStyles={{
            minWidth: 130,
            background: canSubmit
              ? `linear-gradient(90deg, ${theme.palette.customColors.purpleGradientStart} 0%, ${theme.palette.customColors.purpleGradientEnd} 100%)`
              : undefined,
            color: canSubmit ? theme.palette.customColors.white : undefined,
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(AddBalanceModal);
